import _get from 'lodash/get'
import { z } from 'zod'

import { CONTENTS_NXT_CONSTANTS } from '@flows/configs/contentsNxt/constants'
import * as schemaUtils from '@flows/configs/shared/schemaUtils'
import { zipCodeValidatorHookCallback } from '@flows/configs/shared/validatorHooks'
import * as validators from '@flows/configs/shared/validators'
import type { StepConfigParams } from '@flows/configs/types'
import type { AnyStepConfiguration, FlowValues } from '@flows/core/types/stepTypes'
import { BaseSchema_ContentsV1GetsafeDe } from '@flows/insurance/schemas'

const claimsOptionsFromSchema = schemaUtils.getEnumValuesFromPipelineSchema(
  BaseSchema_ContentsV1GetsafeDe.shape.number_of_claims_in_last_5y.unwrap()
)

const contentsInstantSchema = (
  params: StepConfigParams,
  getParamPath: StepConfigParams['helperFunctions']['getParamPath']
) =>
  schemaUtils
    .generateZodObjectFromPaths({
      [getParamPath('living_space_square_meters')]:
        BaseSchema_ContentsV1GetsafeDe.shape.living_space_square_meters,
      born_on: BaseSchema_ContentsV1GetsafeDe.shape.born_on,
      [getParamPath('previous_insurance_in_last_5y')]:
        BaseSchema_ContentsV1GetsafeDe.shape.previous_insurance_in_last_5y.unwrap(),
      [getParamPath('number_of_claims_in_last_5y')]: validators
        .createPreprocessedEnumNumberSchema(z.enum([...claimsOptionsFromSchema, '3']))
        .optional(),
    })
    .superRefine((values, ctx) => {
      if (_get(values, getParamPath('previous_insurance_in_last_5y')) === true) {
        if (typeof _get(values, getParamPath('number_of_claims_in_last_5y')) === 'undefined') {
          ctx.addIssue({
            path: [getParamPath('number_of_claims_in_last_5y')],
            code: z.ZodIssueCode.invalid_type,
            expected: 'integer',
            received: 'undefined',
            message: 'This field is required',
          })
        }
      }
    })

const renters_bundle_contents_instant = (params: StepConfigParams) => {
  const getParamPath = params.helperFunctions.getParamPath
  const constants = CONTENTS_NXT_CONSTANTS

  return {
    schema: contentsInstantSchema(params, getParamPath),
    stepContent: [
      {
        type: 'harmonyTitle',
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_contentsNxt:steps.contents.bundle.instant_step.title',
        },
      },
      {
        type: 'spacer',
        size: 'xsmall',
      },
      {
        type: 'title',
        text: {
          type: 'translatableText',
          translationKey:
            'sharedFlows_contentsNxt:steps.contents.instant_step.living_space_question',
        },
        infoButton: {
          modalConfiguration: [
            {
              type: 'title',
              text: {
                type: 'translatableText',
                translationKey:
                  'sharedFlows_contentsNxt:steps.contents.instant_step.living_space.modal.title',
              },
            },
            {
              type: 'text',
              text: {
                type: 'translatableText',
                translationKey:
                  'sharedFlows_contentsNxt:steps.contents.instant_step.living_space.modal.text1',
              },
            },
            {
              type: 'text',
              text: {
                type: 'translatableText',
                translationKey:
                  'sharedFlows_contentsNxt:steps.contents.instant_step.living_space.modal.text2',
              },
            },
            {
              type: 'text',
              text: {
                type: 'translatableText',
                translationKey:
                  'sharedFlows_contentsNxt:steps.contents.instant_step.living_space.modal.text3',
              },
            },
          ],
        },
      },
      {
        type: 'input',
        name: params.helperFunctions.getParamPath('living_space_square_meters'),
        styleType: 'outlined',
        inputType: 'integer',
        label: {
          type: 'translatableText',
          translationKey:
            'sharedFlows_contentsNxt:steps.contents.instant_step.living_space.input.label',
        },
        autoFocus: true,
      },
      {
        type: 'spacer',
        size: 'mini',
      },
      {
        type: 'title',
        text: {
          type: 'translatableText',
          translationKey:
            'sharedFlows_contentsNxt:steps.contents.bundle.instant_step.birthday_question',
        },
        infoButton: {
          modalConfiguration: [
            {
              type: 'title',
              text: {
                type: 'translatableText',
                translationKey:
                  'sharedFlows_contentsNxt:steps.contents.instant_step.birthday.infobox.title',
              },
            },
            {
              type: 'text',
              text: {
                type: 'translatableText',
                translationKey:
                  'sharedFlows_contentsNxt:steps.contents.instant_step.birthday.infobox.text',
              },
            },
          ],
        },
      },
      {
        type: 'date',
        shouldTrackValues: true,
        name: 'born_on',
        disabledIfValueExists: true,
        autoFocus: false,
      },
      {
        type: 'spacer',
        size: 'mini',
      },
      {
        type: 'title',
        text: {
          type: 'translatableText',
          translationKey:
            'sharedFlows_contentsNxt:steps.contents.bundle.instant_step.previous_insurance_question',
        },
        infoButton: {
          modalConfiguration: [
            {
              type: 'title',
              text: {
                type: 'translatableText',
                translationKey:
                  'sharedFlows_contentsNxt:steps.contents.instant_step.claims.modal.title',
              },
            },
            {
              type: 'text',
              text: {
                type: 'translatableText',
                translationKey:
                  'sharedFlows_contentsNxt:steps.contents.instant_step.claims.modal.text1',
              },
            },
            {
              type: 'text',
              text: {
                type: 'translatableText',
                translationKey:
                  'sharedFlows_contentsNxt:steps.contents.instant_step.claims.modal.text2',
              },
            },
          ],
        },
      },
      {
        type: 'customComponent',
        name: 'ContentsInstantQuoteQuestions',
        props: {
          disabledIfValueExists: true,
          shouldTrackValues: true,
        },
        inputs: [],
        outputs: [],
      },
    ],
    subFlow: params.subFlow,
    onSubmit: {
      type: 'simpleHook',
      inputs: [getParamPath('previous_insurance_in_last_5y')],
      outputs: [
        getParamPath('number_of_claims_in_last_5y'),
        getParamPath('previous_insurance_in_last_5y'),
      ],
      callback: async (flowValues: FlowValues) => {
        const zipCodeValidationError = await zipCodeValidatorHookCallback(params)(flowValues)

        if (zipCodeValidationError) {
          return zipCodeValidationError
        }

        const prevInsurance = _get(flowValues, getParamPath('previous_insurance_in_last_5y'))

        const getRecommendedInsuredSum = (values: FlowValues): number =>
          Number.parseInt(
            z.coerce
              .string()
              .parse(
                _get(values, params.helperFunctions.getParamPath('living_space_square_meters'))
              )
          ) * constants.defaultSquareMeterPrice

        const commonFormValues = {
          [getParamPath('cancelled_by_previous_insurer')]: false,
          [getParamPath('insured_sum_amount')]: getRecommendedInsuredSum(flowValues),
          [getParamPath('permanent_residence_in_germany')]: true,
          [getParamPath('natural_disasters_coverage')]: false,
          [getParamPath('bike_theft_coverage')]: false,
          [getParamPath('glass_coverage')]: false,
          [getParamPath('deductible_amount')]: 0,
          house_no: _get(flowValues, 'houseNumber'),
          street: _get(flowValues, 'street'),
          house_number: _get(flowValues, 'houseNumber'),
          town: _get(flowValues, 'town'),
        }

        if (prevInsurance === false) {
          return {
            formValues: {
              [getParamPath('number_of_claims_in_last_5y')]: 0,
              ...commonFormValues,
            },
          }
        }

        return {
          formValues: commonFormValues,
        }
      },
    },
    nextStep: 'contents_quote',
  } as const satisfies AnyStepConfiguration
}

export default renters_bundle_contents_instant
